import { useEffect, useRef, useState } from 'react'
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
    getZipCode
  } from "use-places-autocomplete";
  import useOnclickOutside from "react-cool-onclickoutside";
  import { useI18n } from '@/lib/I18nProvider';
import AddressModeToggleLink from '@/components/AddressModeToggleLink';
import {
    buildAddressSearchQuery,
    detailsFromGeocodeResult,
    formatManualAddressDescription,
    isIncompletePostalCode,
    parseManualAddressFields,
    pickBestPostalCode,
} from '@/lib/canadianPostalCode';

function localityFromGeocodeResult(result) {
    const components = result?.address_components;
    if (!components?.length) return '';

    const typesPriority = [
        'locality',
        'postal_town',
        'administrative_area_level_3',
        'sublocality',
        'sublocality_level_1',
    ];

    for (const type of typesPriority) {
        const comp = components.find((c) => c.types.includes(type));
        if (comp?.long_name) return comp.long_name;
    }

    return '';
}

function zipFromGeocodeResult(result) {
    return pickBestPostalCode(
        getZipCode(result, false),
        getZipCode(result, true),
        detailsFromGeocodeResult(result).zip,
    );
}

function hasAddressValue(value) {
    return typeof value === 'string' && value.trim() !== '';
}

const emptyManualFields = {
    streetNumber: '',
    street: '',
    postalCode: '',
    city: '',
};

const PlacesAutocomplete = ({
    setSelected,
    parentValue,
    countryCode = 'ca',
    id,
    postalCode,
    city = '',
    latitude = null,
    longitude = null,
    requestManualMode = false,
    onManualModeChange = null,
    allowManualEntry = true,
}) => {
    const { t } = useI18n();
    const [mode, setMode] = useState('autocomplete');
    const [manualFields, setManualFields] = useState(emptyManualFields);
    const prevRequestManualRef = useRef(false);
    const manualFieldsRef = useRef(emptyManualFields);
    const skipAutoOpenRef = useRef(false);
    const geocodeRequestIdRef = useRef(0);
    const lastLatLngRef = useRef({
        lat: latitude ?? null,
        lng: longitude ?? null,
    });
    const {
      ready,
      value,
      suggestions: { status, data },
      setValue,
      clearSuggestions,
      init,
    } = usePlacesAutocomplete({
      requestOptions: {
        types: ['address'],
        componentRestrictions: { country: countryCode },
      },
      debounce: 500,
    });

    const sanitizeManualPart = (part) => (
        hasAddressValue(part) && part.trim().toLowerCase() !== 'false' ? part.trim() : ''
    );

    const openManualMode = (fields = emptyManualFields) => {
        const nextFields = {
            streetNumber: sanitizeManualPart(fields.streetNumber),
            street: sanitizeManualPart(fields.street),
            postalCode: sanitizeManualPart(fields.postalCode),
            city: sanitizeManualPart(fields.city),
        };
        manualFieldsRef.current = nextFields;
        setManualFields(nextFields);
        skipAutoOpenRef.current = false;
        setMode('manual');
        onManualModeChange?.(true);
    };

    const openManualFromCurrentAddress = () => {
        const fromValue = seedManualFields(value || parentValue, postalCode || manualFields.postalCode);
        openManualMode({
            ...manualFields,
            ...fromValue,
            streetNumber: fromValue.streetNumber || manualFields.streetNumber,
            street: fromValue.street || manualFields.street,
            city: fromValue.city || manualFields.city,
            postalCode: fromValue.postalCode || manualFields.postalCode,
        });
    };

    const closeManualMode = () => {
        skipAutoOpenRef.current = true;
        setMode('autocomplete');
        onManualModeChange?.(false);
    };

    const seedManualFields = (address, zip = postalCode) => {
        const parsed = parseManualAddressFields(address, zip);
        return {
            streetNumber: sanitizeManualPart(parsed.streetNumber),
            street: sanitizeManualPart(parsed.street),
            city: sanitizeManualPart(parsed.city || city),
            postalCode: sanitizeManualPart(parsed.postalCode || zip),
        };
    };

    useEffect(() => {
        if (latitude == null || latitude === '' || longitude == null || longitude === '') {
            return;
        }
        lastLatLngRef.current = { lat: latitude, lng: longitude };
    }, [latitude, longitude]);

    // use-places-autocomplete only calls init once; retry until Google Places is available.
    useEffect(() => {
      if (ready) return undefined;
      const timerId = window.setInterval(() => {
        init();
      }, 300);
      return () => window.clearInterval(timerId);
    }, [ready, init]);
    const ref = useOnclickOutside(() => {
      // When user clicks outside of the component, we can dismiss
      // the searched suggestions by calling this method
      //clearSuggestions();
    });

    const applyGeocodeResult = (description, result, preferredPostal = '') => {
        const { lat, lng } = getLatLng(result);
        const details = detailsFromGeocodeResult(result);
        const zipCode = pickBestPostalCode(preferredPostal, details.zip, zipFromGeocodeResult(result));
        const locality = details.city || localityFromGeocodeResult(result);
        const streetAddress = details.streetAddress || description.split(',')[0]?.trim() || description;
        const extra = {
            locality,
            city: locality,
            streetAddress,
            streetNumber: details.streetNumber,
            route: details.route,
            formattedAddress: details.formattedAddress || description,
        };
        lastLatLngRef.current = { lat, lng };
        setSelected(description, { lat, lng }, zipCode, extra);
        const incomplete = isIncompletePostalCode(zipCode, countryCode);
        if (allowManualEntry && incomplete) {
            const parsed = parseManualAddressFields(description, zipCode);
            openManualMode({
                streetNumber: details.streetNumber || parsed.streetNumber,
                street: details.route || parsed.street,
                postalCode: zipCode || parsed.postalCode,
                city: locality || parsed.city || city,
            });
        } else if (!incomplete) {
            closeManualMode();
        }
        return !incomplete;
    };
  
    const handleInput = (e) => {
      setValue(e.target.value);
    };

    const handleManualFieldChange = (field) => (event) => {
        const nextFields = { ...manualFieldsRef.current, [field]: event.target.value };
        manualFieldsRef.current = nextFields;
        setManualFields(nextFields);
    };

    const persistManualFields = (latLng = lastLatLngRef.current) => {
        const fields = manualFieldsRef.current;
        const streetAddress = [fields.streetNumber, fields.street].filter(Boolean).join(' ').trim();
        const description = formatManualAddressDescription({ ...fields, countryCode });
        if (!streetAddress && !fields.city && !fields.postalCode) {
            return;
        }
        setSelected(description || streetAddress, latLng, fields.postalCode, {
            locality: fields.city,
            city: fields.city,
            streetAddress: streetAddress || description,
            streetNumber: fields.streetNumber,
            route: fields.street,
            formattedAddress: description || streetAddress,
        });
    };

    const geocodeManualFields = async () => {
        persistManualFields();
        const fields = manualFieldsRef.current;
        const query = buildAddressSearchQuery({ ...fields, countryCode });
        if (!query) {
            return;
        }
        const requestId = geocodeRequestIdRef.current + 1;
        geocodeRequestIdRef.current = requestId;
        try {
            const results = await getGeocode({ address: query });
            if (requestId !== geocodeRequestIdRef.current) {
                return;
            }
            const result = results[0];
            const description = result?.formatted_address || query;
            setValue(description, false);
            applyGeocodeResult(description, result, fields.postalCode);
        } catch {
            if (requestId !== geocodeRequestIdRef.current) {
                return;
            }
            persistManualFields();
        }
    };
  
    const handleSelect =
      ({ description }) =>
      () => {
        setValue(description, false);
        clearSuggestions();
  
        getGeocode({ address: description })
          .then((results) => {
            const result = results[0];
            applyGeocodeResult(description, result);
          })
          .catch(() => {
            setSelected(description, { lat: null, lng: null }, null, { locality: '' });
            if (allowManualEntry) {
                openManualMode(seedManualFields(description, ''));
            }
          });
    };

    const renderSuggestions = () =>
      data.map((suggestion) => {
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;
  
        return (
          <a key={place_id} onClick={handleSelect(suggestion)} className="dropdown-item p-4" style={{borderBottom: '1px solid #ddd', whiteSpace: 'normal'}}>
            <strong>{main_text}</strong> <small>{secondary_text}</small>
          </a>
        );
    });

    useEffect(() => { 
      if (mode === 'manual') {
        return;
      }
      if (hasAddressValue(parentValue) && parentValue != value){
        setValue(parentValue, false);
        clearSuggestions();
      }
    }, [parentValue]);

    useEffect(() => {
        if (!allowManualEntry || mode === 'manual' || postalCode === undefined) {
            return;
        }
        if (skipAutoOpenRef.current) {
            return;
        }
        const hasAddress = hasAddressValue(parentValue);
        if (hasAddress && isIncompletePostalCode(postalCode, countryCode)) {
            openManualMode(seedManualFields(parentValue, postalCode));
        }
    }, [allowManualEntry, countryCode, mode, parentValue, postalCode]);

    useEffect(() => {
        if (!allowManualEntry) {
            prevRequestManualRef.current = !!requestManualMode;
            return;
        }
        if (requestManualMode && !prevRequestManualRef.current) {
            const parsed = seedManualFields(parentValue, postalCode);
            if (!parsed.street && value) {
                const fromValue = seedManualFields(value, postalCode);
                openManualMode({
                    ...fromValue,
                    ...parsed,
                    street: parsed.street || fromValue.street,
                    streetNumber: parsed.streetNumber || fromValue.streetNumber,
                    city: parsed.city || fromValue.city,
                });
            } else {
                openManualMode(parsed);
            }
        }
        prevRequestManualRef.current = !!requestManualMode;
    }, [allowManualEntry, parentValue, postalCode, requestManualMode, value]);

    if (mode === 'manual' && allowManualEntry) {
        return (
            <div>
                <div className="field mb-3">
                    <label className="label is-size-7 mb-1">{t('common.inputs.streetNumber')}</label>
                    <input
                        className="input"
                        value={manualFields.streetNumber}
                        onChange={handleManualFieldChange('streetNumber')}
                        onBlur={geocodeManualFields}
                        autoComplete="off"
                    />
                </div>
                <div className="field mb-3">
                    <label className="label is-size-7 mb-1">{t('common.inputs.street')}</label>
                    <input
                        className="input"
                        value={manualFields.street}
                        onChange={handleManualFieldChange('street')}
                        onBlur={geocodeManualFields}
                        autoComplete="off"
                    />
                </div>
                <div className="field mb-3">
                    <label className="label is-size-7 mb-1">{t('common.inputs.postalCode')}</label>
                    <input
                        className={`input ${isIncompletePostalCode(manualFields.postalCode, countryCode) ? 'is-danger' : ''}`}
                        value={manualFields.postalCode}
                        onChange={handleManualFieldChange('postalCode')}
                        onBlur={geocodeManualFields}
                        autoComplete="off"
                    />
                    {isIncompletePostalCode(manualFields.postalCode, countryCode) ? (
                        <p className="help is-danger">{t('common.inputs.incompletePostalCode')}</p>
                    ) : null}
                </div>
                <div className="field mb-4">
                    <label className="label is-size-7 mb-1">{t('common.inputs.city')}</label>
                    <input
                        className="input"
                        value={manualFields.city}
                        onChange={handleManualFieldChange('city')}
                        onBlur={geocodeManualFields}
                        autoComplete="off"
                    />
                </div>
                <AddressModeToggleLink onClick={closeManualMode}>
                    {t('common.inputs.automaticEntry')}
                </AddressModeToggleLink>
            </div>
        );
    }
  
    return (
      <div ref={ref}>
        <div className="dropdown is-active is-fullwidth" style={{flexDirection: 'column'}}>
          <div className="dropdown-trigger is-fullwidth">
            <p className="control">
              <input
                id={id}
                className="input control"
                value={hasAddressValue(value) ? value : ''}
                onChange={handleInput}
                disabled={!ready}
                placeholder={t('common.inputs.enterAddress')}
              />
          </p>
        </div>
          {status === "OK" ? ( 
            <div className="dropdown-menu is-fullwidth" id="dropdown-menu" role="menu" style={{position: 'relative'}}>
              <div className="dropdown-content">
                {renderSuggestions()}
              </div>
            </div>
            ) : null
          }
        </div>
        {allowManualEntry && (hasAddressValue(value) || hasAddressValue(parentValue)) ? (
            <AddressModeToggleLink onClick={openManualFromCurrentAddress}>
                {t('common.inputs.addressNotFound')}
            </AddressModeToggleLink>
        ) : null}
      </div>
    );
  };

  export default PlacesAutocomplete
